import { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import { Calendar, CheckCircle, XCircle, ExternalLink, Mail } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const IntegrationSettings = () => {
  const API = useContext(ApiContext);
  const location = useLocation();

  // Calendly states
  const [isCalendlyConnected, setIsCalendlyConnected] = useState(false);
  const [calendlyEmail, setCalendlyEmail] = useState('');
  const [calendlyLoading, setCalendlyLoading] = useState(true);

  // Google/Gmail states
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [googleLoading, setGoogleLoading] = useState(true);

  // Microsoft/Outlook states
  const [isMicrosoftConnected, setIsMicrosoftConnected] = useState(false);
  const [microsoftEmail, setMicrosoftEmail] = useState('');
  const [microsoftLoading, setMicrosoftLoading] = useState(true);

  // Confirmation dialogs states
  const [confirmDialogs, setConfirmDialogs] = useState({
    googleToMicrosoft: false,
    disconnectGoogle: false,
    microsoftToGoogle: false,
    disconnectMicrosoft: false,
    disconnectCalendly: false,
  });

  useEffect(() => {
    checkCalendlyStatus();
    checkGoogleStatus();
    checkMicrosoftStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gérer le callback de Calendly
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const calendlyStatus = params.get('calendly');

    if (calendlyStatus === 'success') {
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const expiresAt = params.get('expires_at');
      const userId = params.get('user_id');
      const email = params.get('email');

      saveCalendlyTokens({ accessToken, refreshToken, expiresAt, userId, email });

      // Nettoyer l'URL
      window.history.replaceState({}, document.title, '/settings');
    } else if (calendlyStatus === 'error') {
      toast.error('Erreur lors de la connexion à Calendly');
      window.history.replaceState({}, document.title, '/settings');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // ========== CALENDLY FUNCTIONS ==========
  const checkCalendlyStatus = async () => {
    try {
      const response = await API.get('/api/calendly/status');
      setIsCalendlyConnected(response.data.isConnected);
      setCalendlyEmail(response.data.email || '');
    } catch (error) {
      console.error('Error checking Calendly status:', error);
    } finally {
      setCalendlyLoading(false);
    }
  };

  const saveCalendlyTokens = async (tokens) => {
    try {
      await API.post('/api/calendly/save-tokens', tokens);
      toast.success('Calendly connecté avec succès');
      setIsCalendlyConnected(true);
      setCalendlyEmail(tokens.email);
    } catch (error) {
      console.error('Error saving Calendly tokens:', error);
      toast.error('Erreur lors de la sauvegarde de la connexion Calendly');
    }
  };

  const handleCalendlyConnect = async () => {
    try {
      const response = await API.get('/api/calendly/auth');
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Error connecting to Calendly:', error);
      toast.error('Erreur lors de la connexion à Calendly');
    }
  };

  const handleCalendlyDisconnect = () => {
    setConfirmDialogs(prev => ({ ...prev, disconnectCalendly: true }));
  };

  const confirmCalendlyDisconnect = async () => {
    try {
      await API.post('/api/calendly/disconnect');
      toast.success('Calendly déconnecté');
      setIsCalendlyConnected(false);
      setCalendlyEmail('');
    } catch (error) {
      console.error('Error disconnecting Calendly:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la déconnexion';
      toast.error(errorMessage);
    }
  };

  // ========== GOOGLE/GMAIL FUNCTIONS ==========
  const checkGoogleStatus = async () => {
    try {
      const response = await API.get('/api/user/google-status');
      setIsGoogleConnected(response.data.isConnected);
      setGoogleEmail(response.data.email || '');
    } catch (error) {
      console.error('Error checking Google status:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleConnect = () => {
    // Vérifier si Microsoft est déjà connecté
    if (isMicrosoftConnected) {
      setConfirmDialogs(prev => ({ ...prev, microsoftToGoogle: true }));
      return;
    }

    // Rediriger vers l'authentification Google OAuth
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    window.location.href = `${backendUrl}/auth/google`;
  };

  const confirmGoogleConnect = async () => {
    // Déconnecter Microsoft d'abord
    try {
      await API.post('/api/user/disconnect-microsoft');
      setIsMicrosoftConnected(false);
      setMicrosoftEmail('');

      // Rediriger vers l'authentification Google OAuth
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      window.location.href = `${backendUrl}/auth/google`;
    } catch (error) {
      console.error('Error disconnecting Microsoft:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la déconnexion de Microsoft';
      toast.error(errorMessage);
    }
  };

  const handleGoogleDisconnect = () => {
    setConfirmDialogs(prev => ({ ...prev, disconnectGoogle: true }));
  };

  const confirmGoogleDisconnect = async () => {
    try {
      const response = await API.post('/api/user/disconnect-google');
      const message = response.data?.message || 'Compte Google déconnecté';

      // Si le message contient "Attention", c'est un warning, afficher plus longtemps
      if (message.includes('Attention')) {
        toast.warning(message, { autoClose: 8000 });
      } else {
        toast.success(message);
      }

      setIsGoogleConnected(false);
      setGoogleEmail('');
    } catch (error) {
      console.error('Error disconnecting Google:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la déconnexion';
      toast.error(errorMessage);
    }
  };

  // ========== MICROSOFT/OUTLOOK FUNCTIONS ==========
  const checkMicrosoftStatus = async () => {
    try {
      const response = await API.get('/api/user/microsoft-status');
      setIsMicrosoftConnected(response.data.isConnected);
      setMicrosoftEmail(response.data.email || '');
    } catch (error) {
      console.error('Error checking Microsoft status:', error);
    } finally {
      setMicrosoftLoading(false);
    }
  };

  const handleMicrosoftConnect = () => {
    // Vérifier si Google est déjà connecté
    if (isGoogleConnected) {
      setConfirmDialogs(prev => ({ ...prev, googleToMicrosoft: true }));
      return;
    }

    // Rediriger vers l'authentification Microsoft OAuth
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    window.location.href = `${backendUrl}/auth/microsoft`;
  };

  const confirmMicrosoftConnect = async () => {
    // Déconnecter Google d'abord
    try {
      await API.post('/api/user/disconnect-google');
      setIsGoogleConnected(false);
      setGoogleEmail('');

      // Rediriger vers l'authentification Microsoft OAuth
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      window.location.href = `${backendUrl}/auth/microsoft`;
    } catch (error) {
      console.error('Error disconnecting Google:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la déconnexion de Google';
      toast.error(errorMessage);
    }
  };

  const handleMicrosoftDisconnect = () => {
    setConfirmDialogs(prev => ({ ...prev, disconnectMicrosoft: true }));
  };

  const confirmMicrosoftDisconnect = async () => {
    try {
      const response = await API.post('/api/user/disconnect-microsoft');
      const message = response.data?.message || 'Compte Microsoft déconnecté';

      // Si le message contient "Attention", c'est un warning, afficher plus longtemps
      if (message.includes('Attention')) {
        toast.warning(message, { autoClose: 8000 });
      } else {
        toast.success(message);
      }

      setIsMicrosoftConnected(false);
      setMicrosoftEmail('');
    } catch (error) {
      console.error('Error disconnecting Microsoft:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la déconnexion';
      toast.error(errorMessage);
    }
  };

  if (calendlyLoading || googleLoading || microsoftLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border p-6">
          <p className="text-sm text-gray-500">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Google/Gmail */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Mail className="text-red-600" size={24} />
            <div>
              <h3 className="text-lg font-semibold m-0">Gmail / Outlook</h3>
              <p className="text-sm text-gray-500">
                Connectez votre compte Google pour envoyer des emails depuis Jobrise
              </p>
            </div>
          </div>

          {isGoogleConnected ? (
            <CheckCircle className="text-green-600" size={24} />
          ) : (
            <XCircle className="text-gray-400" size={24} />
          )}
        </div>

        {isGoogleConnected ? (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">
                  Compte Google connecté
                </p>
                <p className="text-sm text-green-600">{googleEmail}</p>
                <p className="text-xs text-green-600 mt-1">
                  ✓ Accès Gmail activé
                </p>
              </div>
              <button
                onClick={handleGoogleDisconnect}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                Déconnecter
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Pourquoi connecter votre compte Google ?</strong>
              </p>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>Envoyez des emails directement depuis Jobrise</li>
                <li>Synchronisez vos contacts et calendrier</li>
                <li>Accédez à Gmail API pour une meilleure intégration</li>
              </ul>
            </div>
            <button
              onClick={handleGoogleConnect}
              className="flex items-center space-x-2 px-4 py-2 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Se connecter avec Google</span>
            </button>
          </div>
        )}
      </div>

      {/* Section Microsoft/Outlook */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Mail className="text-blue-600" size={24} />
            <div>
              <h3 className="text-lg font-semibold m-0">Microsoft / Outlook</h3>
              <p className="text-sm text-gray-500">
                Connectez votre compte Microsoft pour envoyer des emails depuis Jobrise
              </p>
            </div>
          </div>

          {isMicrosoftConnected ? (
            <CheckCircle className="text-green-600" size={24} />
          ) : (
            <XCircle className="text-gray-400" size={24} />
          )}
        </div>

        {isMicrosoftConnected ? (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">
                  Compte Microsoft connecté
                </p>
                <p className="text-sm text-green-600">{microsoftEmail}</p>
                <p className="text-xs text-green-600 mt-1">
                  ✓ Accès Outlook activé
                </p>
              </div>
              <button
                onClick={handleMicrosoftDisconnect}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                Déconnecter
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Pourquoi connecter votre compte Microsoft ?</strong>
              </p>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>Envoyez des emails directement depuis Jobrise</li>
                <li>Synchronisez vos contacts Outlook</li>
                <li>Accédez à Outlook API pour une meilleure intégration</li>
              </ul>
            </div>
            <button
              onClick={handleMicrosoftConnect}
              className="flex items-center space-x-2 px-4 py-2 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 23 23">
                <path fill="#f25022" d="M0 0h11v11H0z"/>
                <path fill="#00a4ef" d="M12 0h11v11H12z"/>
                <path fill="#7fba00" d="M0 12h11v11H0z"/>
                <path fill="#ffb900" d="M12 12h11v11H12z"/>
              </svg>
              <span>Se connecter avec Microsoft</span>
            </button>
          </div>
        )}
      </div>

      {/* Section Calendly */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calendar className="text-blue-600" size={24} />
            <div>
              <h3 className="text-lg font-semibold m-0">Calendly</h3>
              <p className="text-sm text-gray-500">
                Connectez votre compte Calendly pour synchroniser vos rendez-vous
              </p>
            </div>
          </div>

          {isCalendlyConnected ? (
            <CheckCircle className="text-green-600" size={24} />
          ) : (
            <XCircle className="text-gray-400" size={24} />
          )}
        </div>

        {isCalendlyConnected ? (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">
                  Compte connecté
                </p>
                <p className="text-sm text-green-600">{calendlyEmail}</p>
              </div>
              <button
                onClick={handleCalendlyDisconnect}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                Déconnecter
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <button
              onClick={handleCalendlyConnect}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <ExternalLink size={16} />
              <span>Connecter Calendly</span>
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Dialogs */}

      {/* Déconnecter Microsoft pour connecter Google */}
      <ConfirmationDialog
        isOpen={confirmDialogs.microsoftToGoogle}
        onClose={() => setConfirmDialogs(prev => ({ ...prev, microsoftToGoogle: false }))}
        onConfirm={confirmGoogleConnect}
        title="Changer de service d'email"
        message="Vous êtes déjà connecté avec Microsoft. Voulez-vous déconnecter Microsoft et connecter Google à la place ?"
        confirmText="Oui, changer"
        cancelText="Annuler"
        isDangerous={false}
      />

      {/* Déconnecter Google */}
      <ConfirmationDialog
        isOpen={confirmDialogs.disconnectGoogle}
        onClose={() => setConfirmDialogs(prev => ({ ...prev, disconnectGoogle: false }))}
        onConfirm={confirmGoogleDisconnect}
        title="Déconnecter Google"
        message="Êtes-vous sûr de vouloir déconnecter votre compte Google ? Cela supprimera l'accès à Gmail."
        confirmText="Déconnecter"
        cancelText="Annuler"
        isDangerous={true}
      />

      {/* Déconnecter Google pour connecter Microsoft */}
      <ConfirmationDialog
        isOpen={confirmDialogs.googleToMicrosoft}
        onClose={() => setConfirmDialogs(prev => ({ ...prev, googleToMicrosoft: false }))}
        onConfirm={confirmMicrosoftConnect}
        title="Changer de service d'email"
        message="Vous êtes déjà connecté avec Google. Voulez-vous déconnecter Google et connecter Microsoft à la place ?"
        confirmText="Oui, changer"
        cancelText="Annuler"
        isDangerous={false}
      />

      {/* Déconnecter Microsoft */}
      <ConfirmationDialog
        isOpen={confirmDialogs.disconnectMicrosoft}
        onClose={() => setConfirmDialogs(prev => ({ ...prev, disconnectMicrosoft: false }))}
        onConfirm={confirmMicrosoftDisconnect}
        title="Déconnecter Microsoft"
        message="Êtes-vous sûr de vouloir déconnecter votre compte Microsoft ? Cela supprimera l'accès à Outlook."
        confirmText="Déconnecter"
        cancelText="Annuler"
        isDangerous={true}
      />

      {/* Déconnecter Calendly */}
      <ConfirmationDialog
        isOpen={confirmDialogs.disconnectCalendly}
        onClose={() => setConfirmDialogs(prev => ({ ...prev, disconnectCalendly: false }))}
        onConfirm={confirmCalendlyDisconnect}
        title="Déconnecter Calendly"
        message="Êtes-vous sûr de vouloir déconnecter Calendly ?"
        confirmText="Déconnecter"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default IntegrationSettings;
